/**
 * Status Toggle Button Component
 * 
 * Reusable button component for toggling entity status (activate/deactivate).
 * Includes appropriate icons, colors, and tooltips based on current status.
 * 
 * @module StatusToggleButton
 */

import React from 'react';
import { IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';
import { useTheme } from '../../ThemeContext';
import { useDetectMyRole } from '../../custom-hooks';
import ThemedTooltip from './ThemedTooltip';
import { MODULE_PERMISSIONS } from '../../constants/filter-constants';

/**
 * StatusToggleButton Component
 * 
 * @param {Object} props - Component props
 * @param {Object} props.entity - Entity object with isActive property
 * @param {string} props.module - Module name ('facility', 'user', 'provider')
 * @param {Function} props.onToggle - Toggle handler function
 * @param {boolean} [props.size='small'] - Button size
 * @param {Object} [props.sx] - Additional styling
 * @returns {JSX.Element} StatusToggleButton component
 * 
 * @example
 * <StatusToggleButton
 *   entity={facility}
 *   module="facility"
 *   onToggle={(entity, action) => handleToggle(entity, action)}
 * />
 */
const StatusToggleButton = ({
  entity,
  module,
  onToggle,
  size = 'small',
  sx = {},
  ...otherProps
}) => {
  const { currentTheme } = useTheme();
  const { role } = useDetectMyRole();

  /**
   * Checks if user has permission to toggle status
   * 
   * @returns {boolean} Whether user has permission
   */
  const hasPermission = () => {
    const modulePermissions = MODULE_PERMISSIONS[module];
    if (!modulePermissions || !modulePermissions.toggleStatus) {
      return role === 'admin'; // Default to admin-only
    }
    return modulePermissions.toggleStatus.includes(role);
  };

  const canToggle = hasPermission();
  const isActive = entity?.isActive;
  const action = isActive ? 'deactivate' : 'activate';

  /**
   * Gets appropriate color based on action and permissions
   * 
   * @returns {string} Color value
   */
  const getColor = () => {
    if (!canToggle) {
      return currentTheme.text.disabled || '#999';
    }
    return isActive ? '#f57c00' : '#2e7d32'; // Orange for deactivate, green for activate
  };

  /**
   * Gets tooltip text based on permissions and action
   * 
   * @returns {string} Tooltip text
   */
  const getTooltipText = () => {
    if (!canToggle) {
      return `Administrator privileges required to change ${module} status`;
    }
    return `${action.charAt(0).toUpperCase() + action.slice(1)} ${module}`;
  };

  /**
   * Gets appropriate icon based on current status
   * 
   * @returns {JSX.Element} Icon component
   */
  const getIcon = () => {
    return isActive ? (
      <DeleteIcon fontSize={size} />
    ) : (
      <RestoreIcon fontSize={size} />
    );
  };

  /**
   * Handles button click
   */
  const handleClick = () => {
    if (canToggle && onToggle) {
      onToggle(entity, action);
    }
  };

  return (
    <ThemedTooltip 
      title={getTooltipText()}
      placement="top"
    >
      <span>
        <IconButton
          aria-label={action}
          onClick={handleClick}
          size={size}
          disabled={!canToggle}
          sx={{ 
            color: getColor(),
            cursor: canToggle ? 'pointer' : 'not-allowed',
            ml: 0.5,
            ...sx
          }}
          {...otherProps}
        >
          {getIcon()}
        </IconButton>
      </span>
    </ThemedTooltip>
  );
};

export default StatusToggleButton;
